/**
 * NumberField -- Dynamic numeric input field.
 *
 * Renders a number input that honours min_value, max_value, and step from the
 * field schema. Uses the shadcn/ui Input component with `type="number"`.
 * ARIA attributes communicate the valid range to assistive technology.
 */

import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { DynamicFieldProps } from './index';

export function NumberField({ field, value, onChange, error, readOnly }: DynamicFieldProps) {
  const inputId = `field-${field.name}`;
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;

  const describedBy = [
    field.help_text ? helpId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  /**
   * Coerce the incoming value to a numeric string for the input.
   * Empty string keeps the field blank rather than showing "0".
   */
  const numericValue =
    value === '' || value === null || value === undefined
      ? ''
      : String(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange('');
      return;
    }
    const parsed = Number(raw);
    onChange(Number.isNaN(parsed) ? raw : parsed);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label htmlFor={inputId}>
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="text-destructive ml-1">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>

        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <Input
        id={inputId}
        type="number"
        value={numericValue}
        onChange={handleChange}
        placeholder={field.placeholder ?? undefined}
        disabled={readOnly}
        min={field.min_value ?? undefined}
        max={field.max_value ?? undefined}
        step={field.step ?? undefined}
        aria-required={field.required}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        aria-valuemin={field.min_value ?? undefined}
        aria-valuemax={field.max_value ?? undefined}
        className={cn(error && 'border-destructive focus-visible:ring-destructive')}
      />

      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
